/**
 * Country flag with the full country name on hover. One place for every
 * table or row that shows an ISO 3166-1 alpha-2 code, so a merchant reads
 * "🇩🇪 Germany" rather than "DE" — never the raw code. `ZZ` (unknown)
 * renders a muted block.
 */

import { Tooltip } from './Tooltip.js';

let display_names: Intl.DisplayNames | null | undefined;

export function countryName(code: string | null | undefined): string | null {
  if (!code || code.length !== 2) return null;
  const upper = code.toUpperCase();
  if (upper === 'ZZ') return 'Unknown country';
  if (display_names === undefined) {
    try {
      display_names = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      display_names = null;
    }
  }
  try {
    const name = display_names?.of(upper);
    return typeof name === 'string' && name !== upper ? name : upper;
  } catch {
    return upper;
  }
}

export interface CountryFlagProps {
  readonly code: string | null | undefined;
  /** Also print the country name after the flag (default: flag only, name in the tooltip). */
  readonly withName?: boolean | undefined;
  /** Class for the printed name, e.g. `hidden sm:inline` to collapse to the flag on phones. */
  readonly nameClassName?: string | undefined;
  readonly size?: 16 | 20 | undefined;
}

export function CountryFlag({ code, withName = false, nameClassName, size = 16 }: CountryFlagProps): JSX.Element | null {
  if (!code || code.length !== 2) return null;
  const upper = code.toUpperCase();
  const name = countryName(upper) ?? upper;
  const h = size === 20 ? 15 : 12;
  const img = upper === 'ZZ'
    ? <span aria-hidden="true" style={{ display: 'inline-block', width: size, height: h, borderRadius: 1, background: 'currentColor', opacity: 0.15 }} />
    : (
      <img
        src={`https://flagcdn.com/${size === 20 ? '20x15' : '16x12'}/${upper.toLowerCase()}.png`}
        alt={name}
        width={size}
        height={h}
        style={{ display: 'inline-block', verticalAlign: 'text-bottom', borderRadius: 1 }}
      />
    );
  return (
    <Tooltip content={name} displayMode="inline-flex">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'default' }} aria-label={name}>
        {img}
        {withName ? <span className={nameClassName}>{name}</span> : null}
      </span>
    </Tooltip>
  );
}
